import { Component, DestroyRef, inject, Input, OnInit, computed } from '@angular/core';
import { Brand, BrandManagementColumn, BrandSummary } from '../types';
import { ClientSideRowModelModule, GridOptions, RowClickedEvent, themeQuartz, type ColDef, SortChangedEvent } from "ag-grid-community";
import { MatDialog } from '@angular/material/dialog';
import { AgGridAngular } from 'ag-grid-angular';
import { AgTooltipCellComponent } from '../../../shared/components/ag-tooltip-cell/ag-tooltip-cell.component';
import { BrandModalComponent } from '../brand-modal/brand-modal.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StateService } from '../../../services/state.service';
import { ManagementService } from '../management.service';
import { BrandNameLogoRendererComponent } from '../../brands-dashboard-screen/dashboard-table/custom-table-components/brand-name-logo/brand-name-logo-renderer.component';
import { UtilService } from '../../../services/util.service';
import {getManagementDateFormat} from '../../../utils/time';

@Component({
  selector: 'app-brands-table',
  imports: [AgGridAngular],
  templateUrl: './brands-table.component.html',
  styleUrl: './brands-table.component.scss'
})
export class BrandsTableComponent {

  data = computed(() => this.managementService.tableData() as Brand[]);
  colDefs: ColDef[] = this.getColDef();
  theme = themeQuartz.withParams({
    spacing: 10,
    headerBackgroundColor: 'rgba(217, 217, 217, 0.3)',
    rowHoverColor: 'rgba(233, 244, 245, 0.85)',
    fontSize: '14px',
    headerFontSize: '16px',
    fontFamily: 'Poppins',
    headerFontFamily: 'Poppins',
    headerFontWeight: 600,
    headerTextColor: '#717172;',
    cellTextColor: '#717172;',
    headerHeight: 64,
  });
  modules = [ClientSideRowModelModule];
  gridOptions: GridOptions = {
    suppressMovableColumns: true,
    domLayout: 'autoHeight',
    rowHeight: undefined,
    defaultColDef: {
      resizable: false,
      autoHeight: true,
      sortable: true,
      wrapText: true,
      flex: 1
    },
  };
  private destroyRef = inject(DestroyRef);

  constructor(private dialog: MatDialog, private stateService: StateService,
    private managementService: ManagementService, private utilService: UtilService) { }

  onRowClicked = (event: RowClickedEvent) => {
    const dialogRef = this.dialog.open(BrandModalComponent, {
      data: {
        ...event.data
      },
      panelClass: 'management-modal',
      autoFocus: false
    });
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result: Brand) => {
      if (result) {
        const currentData = this.data();
        const index = currentData.findIndex(brand => brand.brandKey === result.brandKey);
        if (index !== -1) {
          const updatedTableData = [...currentData];
          updatedTableData[index] = result;
          this.managementService.tableData.set(updatedTableData);
          this.managementService.updateBrandInList({ name: result.name, brandKey: result.brandKey });
          this.utilService.openSnackBar(`Brand "${result.name}" updated successfuly`, { position: 'center', variant: 'success' });
        }
      }
      this.stateService.loading = false;
    });
  };

  public onSortChanged(event: SortChangedEvent) {
    const sortModel = event.columns?.[0]
    this.managementService.sortState.set({
      sortBy: sortModel?.getColId()|| '',
      sortDir: sortModel?.getSort() || 'desc'
    });
    this.managementService.refreshTable$.next();
  }

  private getColDef() {
    return [
      {
        field: BrandManagementColumn.BRAND_KEY,
        cellRenderer: BrandNameLogoRendererComponent,
        valueGetter: (params: any) => {
          return {
            name: params.data.brandKey,
            logoPath: params.data.logoPath
          };
        },
      },
      {
        field: BrandManagementColumn.BRAND_NAME,
      },
      {
        field: BrandManagementColumn.ACCOUNTS,
        headerName: "Accounts",
        cellRenderer: AgTooltipCellComponent,
        valueGetter: (params: any) => {
          const domains = params.data?.[BrandManagementColumn.ACCOUNTS];
          return domains.join(', ');
        },
        sortable: false,
      },
      {
        field: BrandManagementColumn.KEYWORDS,
        cellRenderer: AgTooltipCellComponent,
        valueGetter: (params: any) => {
          const keywords = params.data?.[BrandManagementColumn.KEYWORDS];
          return keywords.join(', ');
        },
        sortable: false,
      },
      {
        field: BrandManagementColumn.ADVERTISER_DOMAINS,
        cellRenderer: AgTooltipCellComponent,
        valueGetter: (params: any) => {
          const domains = params.data?.[BrandManagementColumn.ADVERTISER_DOMAINS];
          return domains.join(', ');
        },
        sortable: false,
      },
      {
        field: BrandManagementColumn.COMPETITORS,
        headerName: "Competitors",
        cellRenderer: AgTooltipCellComponent,
        valueGetter: (params: any) => {
          return params.data[BrandManagementColumn.COMPETITORS]
            .map((item: BrandSummary) => item?.name)
            .filter(Boolean)
            .join(', ');
        },
        sortable: false,
      },
      {
        field: BrandManagementColumn.CREATED_AT,
        cellRenderer: AgTooltipCellComponent,
        valueGetter: (params: any) => {
          return getManagementDateFormat(params.data[BrandManagementColumn.CREATED_AT])
        }
      }
    ];
  }
}
